// Fuzzball Quiz State Management
// Centralized state management for all quiz data

window.QuizState = {
  // Current state data
  data: {
    submissionData: {
      // Cat-specific data stored as cat1, cat2, etc.
      cat1: {
        fuzzballer_name: '',
        breed: '',
        age_range: '',
        wet_dry_preference: '',
        cans_per_day: '',
        health_concerns_exists: '',
        health_concerns: '',
        lifestyle: '',
        disliked_flavors: []
      },
      // Global data
      catCount: 1,
      customer_name: '',
      customer_email: ''
    },
    recommendations: {},
    selections: {
      bundle: [],
      trail: [],
      extras: [],
      // Per-cat flags marking whether the user has manually edited the surface
      // since the last quiz answer change. False/undefined = surface still
      // reflects auto-populated recommendations and can be re-applied freely
      // on step-7 re-entry. True = user has touched it and must be preserved.
      // Shape: { cat1: true, cat2: false, ... } — per-cat for bundle and
      // trail; extras mirrors the per-cat shape because QuizExtrasManager
      // stores extras per cat (cat1/cat2/cat3/cat4).
      bundleUserEdited: {},
      trailUserEdited: {},
      extrasUserEdited: {}
    },
    quiz: {
      currentStep: 1,
      totalSteps: 7,
      extrasPopupShown: false,
      isEditMode: false,
      editQuizId: null,
      editType: null,
      completed: false,
      klaviyoSubmitted: false
    }
  },

  // Event listeners for state changes
  listeners: {},

  // Initialize state from localStorage
  init() {
    this.loadAll();
    
    // Ensure data structure migration for existing users
    if (this.data.submissionData && this.data.submissionData.fuzzballer_name && !this.data.submissionData.cat1) {
      // Migrate from old single-cat structure to new multi-cat structure
      const oldData = { ...this.data.submissionData };
      this.data.submissionData = {
        cat1: {
          fuzzballer_name: oldData.fuzzballer_name || '',
          breed: oldData.breed || '',
          age_range: oldData.age_range || '',
          wet_dry_preference: oldData.wet_dry_preference || '',
          cans_per_day: oldData.cans_per_day || '',
          health_concerns_exists: oldData.health_concerns_exists || '',
          health_concerns: oldData.health_concerns || '',
          lifestyle: oldData.lifestyle || '',
          disliked_flavors: oldData.disliked_flavors || []
        },
        catCount: 1,
        customer_name: oldData.customer_name || '',
        customer_email: oldData.customer_email || ''
      };
      this.save('submissionData', this.data.submissionData);
    }
  },

  // Save data to state and localStorage
  save(key, value) {
    if (typeof key === 'object') {
      // Allow saving multiple keys at once
      Object.entries(key).forEach(([k, v]) => {
        this.data[k] = v;
        localStorage.setItem(k, JSON.stringify(v));
      }); 
    } else {
      this.data[key] = value;
      localStorage.setItem(key, JSON.stringify(value));
    }
    this.emit(`${key}:changed`, value);
  },

  // Load data from localStorage
  load(key) {
    const stored = localStorage.getItem(key);
    if (stored) {
      try {
        this.data[key] = JSON.parse(stored);
      } catch (e) {
      }
    }
    return this.data[key];
  },

  // Load all quiz data from localStorage
  loadAll() {
    const keys = ['submissionData', 'recommendations', 'quizRecommendations', 'selectedExtras', 'quiz_submitted',
                  'editModeProducts', 'allEditProducts', 'trailBoxSelections', 'klaviyo_submitted'];

    keys.forEach(key => {
      const value = this.load(key);
      if (value) {
        if (key === 'quizRecommendations' || key === 'recommendations') {
          this.data.recommendations = value;
        } else if (key === 'selectedExtras') {
          this.data.selections.extras = value;
        } else if (key === 'quiz_submitted') {
          this.data.quiz.completed = value === 'true' || value === true;
        } else if (key === 'klaviyo_submitted') {
          this.data.quiz.klaviyoSubmitted = value === 'true' || value === true;
        }
      }
    });

    // Hydrate user-edited flags from step10Selections (the existing source-of-
    // truth localStorage key used by quiz-bundle-builder-v2.js and
    // quiz-trail-box-v2.js to round-trip selections). No new localStorage key
    // needed — we nest these alongside `bundle` / `trailBox` / `extras` keys
    // that the other modules already manage.
    try {
      const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
      this.data.selections.bundleUserEdited = step10Data.bundleUserEdited || {};
      this.data.selections.trailUserEdited = step10Data.trailUserEdited || {};
      this.data.selections.extrasUserEdited = step10Data.extrasUserEdited || {};
    } catch (e) {
      this.data.selections.bundleUserEdited = {};
      this.data.selections.trailUserEdited = {};
      this.data.selections.extrasUserEdited = {};
    }
  },

  // Mark a selection surface as user-edited for a given cat. Called by the
  // bundle / trail-box / extras modules when the user actively changes a
  // value (not on programmatic auto-populate). Persists to step10Selections
  // so the flag round-trips across page loads.
  markSelectionUserEdited(surface, catKey) {
    // surface = 'bundle' | 'trail' | 'extras'
    const flagKey = `${surface}UserEdited`;
    if (!this.data.selections[flagKey]) this.data.selections[flagKey] = {};
    if (!catKey) catKey = 'cat1';

    // GA4: fire quiz_box_edited the first time each surface+cat is edited in this
    // page session. This is the single user-edit choke-point (bundle +/-, typed
    // quantity, extras add/remove, trail box) and is never hit on programmatic
    // auto-populate — so the signal is noise-free. In-memory guard (not the
    // persisted flag) so it counts sessions-that-edit, independent of prior visits.
    if (!this._boxEditFired) this._boxEditFired = {};
    const fireKey = `${surface}:${catKey}`;
    if (!this._boxEditFired[fireKey]) {
      this._boxEditFired[fireKey] = true;
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'quiz_box_edited', { edit_type: surface, cat: catKey });
        }
      } catch (e) {}
    }

    this.data.selections[flagKey][catKey] = true;

    try {
      const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
      step10Data[flagKey] = this.data.selections[flagKey];
      step10Data.timestamp = Date.now();
      localStorage.setItem('step10Selections', JSON.stringify(step10Data));
    } catch (e) {
      // Best-effort; in-memory state is still correct for the current session.
    }
  },

  // Clear data from state and localStorage
  clear(key) {
    if (Array.isArray(key)) {
      key.forEach(k => {
        delete this.data[k];
        localStorage.removeItem(k);
      });
    } else {
      delete this.data[key];
      localStorage.removeItem(key);
    }
  },

  // Get current state value
  get(key) {
    if (key.includes('.')) {
      // Support nested keys like 'quiz.currentStep'
      const keys = key.split('.');
      let value = this.data;
      for (const k of keys) {
        value = value?.[k];
      }
      return value;
    }
    return this.data[key];
  },

  // Set state value without saving to localStorage
  set(key, value) {
    if (key.includes('.')) {
      // Support nested keys
      const keys = key.split('.');
      const lastKey = keys.pop();
      let obj = this.data;
      for (const k of keys) {
        obj = obj[k] = obj[k] || {};
      }
      obj[lastKey] = value;
    } else {
      this.data[key] = value;
    }
    this.emit(`${key}:changed`, value);
  },

  // Event emitter functionality
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  },

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    }
  },

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
        }
      });
    }
  },

  // Helper methods for common operations
  updateSubmissionData(field, value) {
    this.data.submissionData[field] = value;
    this.save('submissionData', this.data.submissionData);
  },

  updateRecommendations(recommendations) {
    this.data.recommendations = recommendations;
    this.save('recommendations', recommendations);
    // Also save to legacy key for compatibility
    this.save('quizRecommendations', recommendations);
  },

  addBundleSelection(product) {
    const existing = this.data.selections.bundle.findIndex(p => p.id === product.id);
    if (existing >= 0) {
      this.data.selections.bundle[existing] = product;
    } else {
      this.data.selections.bundle.push(product);
    }
    this.emit('bundle:updated', this.data.selections.bundle);
  },

  removeBundleSelection(productId) {
    this.data.selections.bundle = this.data.selections.bundle.filter(p => p.id !== productId);
    this.emit('bundle:updated', this.data.selections.bundle);
  },

  clearBundleSelections() {
    this.data.selections.bundle = [];
    this.emit('bundle:updated', this.data.selections.bundle);
  },

  isQuizComplete() {
    return this.data.quiz.completed || localStorage.getItem('quiz_submitted') === 'true';
  },

  markQuizComplete() {
    this.data.quiz.completed = true;
    this.save('quiz_submitted', 'true');
  },

  resetQuiz() {
    // Clear all quiz-related data
    this.clear(['submissionData', 'quizRecommendations', 'selectedExtras',
                'quiz_submitted', 'editModeProducts', 'allEditProducts', 'trailBoxSelections',
                'klaviyo_submitted']);

    // Clear the user-edited flags from step10Selections without nuking the
    // whole key (other modules co-own bundle/trailBox/extras fields under the
    // same key — leaving those alone matches the existing resetQuiz contract).
    try {
      const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
      delete step10Data.bundleUserEdited;
      delete step10Data.trailUserEdited;
      delete step10Data.extrasUserEdited;
      localStorage.setItem('step10Selections', JSON.stringify(step10Data));
    } catch (e) {
      // Best-effort
    }

    // Reset state
    this.data = {
      submissionData: {
        cat1: {
          fuzzballer_name: '',
          breed: '',
          age_range: '',
          wet_dry_preference: '',
          cans_per_day: '',
          health_concerns_exists: '',
          health_concerns: '',
          lifestyle: '',
          disliked_flavors: []
        },
        catCount: 1,
        customer_name: '',
        customer_email: ''
      },
      recommendations: {},
      selections: {
        bundle: [],
        trail: [],
        extras: [],
        bundleUserEdited: {},
        trailUserEdited: {},
        extrasUserEdited: {}
      },
      quiz: {
        currentStep: 1,
        totalSteps: 7,
        extrasPopupShown: false,
        isEditMode: false,
        editQuizId: null,
        editType: null,
        completed: false,
        klaviyoSubmitted: false
      }
    };

    this.emit('quiz:reset', true);
  },

  // Multi-cat helper methods
  addCat() {
    const catCount = this.data.submissionData.catCount || 1;
    if (catCount >= 3) {
      return false;
    }
    
    const newCatNumber = catCount + 1;
    this.data.submissionData[`cat${newCatNumber}`] = {
      fuzzballer_name: '',
      breed: '',
      age_range: '',
      wet_dry_preference: '',
      cans_per_day: '',
      health_concerns_exists: '',
      health_concerns: '',
      lifestyle: '',
      disliked_flavors: []
    };
    
    this.data.submissionData.catCount = newCatNumber;
    this.save('submissionData', this.data.submissionData);
    
    return newCatNumber;
  },

  getCat(catNumber) {
    return this.data.submissionData[`cat${catNumber}`];
  },

  updateCatData(catNumber, field, value) {
    // Ensure cat exists
    if (!this.data.submissionData[`cat${catNumber}`]) {
      this.data.submissionData[`cat${catNumber}`] = {};
      
      // Also ensure catCount is correct
      const currentCatCount = this.data.submissionData.catCount || 0;
      if (catNumber > currentCatCount) {
        this.data.submissionData.catCount = catNumber;
      }
    }
    
    this.data.submissionData[`cat${catNumber}`][field] = value;
    this.save('submissionData', this.data.submissionData);
  },

  getAllCats() {
    const cats = [];
    const catCount = this.data.submissionData.catCount || 1;
    
    for (let i = 1; i <= catCount; i++) {
      if (this.data.submissionData[`cat${i}`]) {
        cats.push({
          number: i,
          data: this.data.submissionData[`cat${i}`]
        });
      }
    }
    
    return cats;
  }
};

// Initialize the state when the script loads
window.QuizState.init();
